import { useCallback, useEffect, useState } from 'react';
import { SurveyFormatRef, UseActiveViewReturn } from './types';
import { findFirstFocusable, scrollIntoView } from './utils';

const useActiveView = (views: SurveyFormatRef[]): UseActiveViewReturn => {
  const [viewsState, setViewsState] = useState(views);
  const [activeView, setActiveView] = useState<SurveyFormatRef | null>(null);

  useEffect(() => {
    setViewsState(views);
  }, [views]);

  const setFocus = useCallback(
    (name: string) => {
      const view = viewsState.find(v => v.name === name);
      if (view?.ref?.current) {
        const firstFocusable = findFirstFocusable(view.ref.current);
        firstFocusable?.focus();
      }
    },
    [viewsState]
  );

  const setNextActive = useCallback(() => {
    const idx = viewsState.findIndex(v => v.name === activeView?.name);
    const nextActive = viewsState[idx + 1] || null;
    if (nextActive?.ref?.current) {
      setFocus(nextActive.name);
    }
  }, [viewsState, activeView, setFocus]);

  const scrollToActive = useCallback(
    (name: string) => {
      const view = viewsState.find(v => v.name === name);
      const clickShield = view?.ref?.current;
      if (clickShield) {
        const target = (clickShield.firstElementChild ?? clickShield) as HTMLElement;
        scrollIntoView(target);
      }
    },
    [viewsState]
  );

  const setActive = useCallback(
    (name: string | null, direct?: boolean) => {
      if (name !== null) {
        if (direct) {
          const ref = viewsState.find(view => view.name === name) || null;
          setActiveView(ref);
        } else {
          scrollToActive(name);
        }
      }
    },
    [viewsState, scrollToActive]
  );

  return {
    active: activeView,
    setActive,
    setNextActive,
  };
};

export default useActiveView;
